import { useCallback, useEffect, useState } from 'react';
import { isPushSupported, isStandalone, isIosSafari } from '../utils/platform';
import { urlBase64ToUint8Array } from '../utils/vapid';
import {
  useLazyGetVapidKeyQuery,
  useSubscribePushMutation,
  useUnsubscribePushMutation,
} from '../store/api/localApi';

const LAST_ENDPOINT_KEY = 'sproutly.push.lastEndpoint';

export interface PushState {
  isSupported: boolean;
  isStandalone: boolean;
  isIosSafari: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<void>;
}

export function usePushNotifications(): PushState {
  const supported = isPushSupported();
  const [permission, setPermission] = useState<NotificationPermission>(
    supported ? Notification.permission : 'denied'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);

  const [fetchVapidKey] = useLazyGetVapidKeyQuery();
  const [subscribePush] = useSubscribePushMutation();
  const [unsubscribePush] = useUnsubscribePushMutation();

  // Reflect the current subscription state on mount (no network).
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => {
        if (!cancelled) setIsSubscribed(!!sub);
      })
      .catch(() => {
        /* SW not ready / unsupported — leave isSubscribed false */
      });
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!supported) return false;
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return false;

      const { publicKey } = await fetchVapidKey().unwrap();
      const reg = await navigator.serviceWorker.ready;

      // Reuse an existing subscription if present; else create one.
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          // Cast to BufferSource: the DOM types want ArrayBuffer, but the Uint8Array
          // we build is a valid BufferSource at runtime (lib.dom generic friction).
          applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
        });
      }

      const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
      await subscribePush({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      }).unwrap();

      localStorage.setItem(LAST_ENDPOINT_KEY, json.endpoint);
      setIsSubscribed(true);
      return true;
    } catch (err) {
      // Non-fatal: caller surfaces a soft error. Never throw to the UI.
      // eslint-disable-next-line no-console
      console.error('push subscribe failed', err);
      return false;
    }
  }, [supported, fetchVapidKey, subscribePush]);

  const unsubscribe = useCallback(async (): Promise<void> => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await unsubscribePush({ endpoint })
          .unwrap()
          .catch(() => undefined);
      }
      localStorage.removeItem(LAST_ENDPOINT_KEY);
      setIsSubscribed(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('push unsubscribe failed', err);
    }
  }, [supported, unsubscribePush]);

  return {
    isSupported: supported,
    isStandalone: isStandalone(),
    isIosSafari: isIosSafari(),
    permission,
    isSubscribed,
    subscribe,
    unsubscribe,
  };
}
